import { getEndCursor } from 'components/Blocks/Pagination/paginationHelpers';
import Breadcrumbs from 'components/Layout/Breadcrumbs/Breadcrumbs';
import CommonLayout from 'components/Layout/CommonLayout';
import { Webline } from 'components/Layout/Webline/Webline';
import FlagDetail from 'components/Pages/FlagDetail/FlagDetail';
import {
    FILTER_QUERY_PARAMETER_NAME,
    LOAD_MORE_QUERY_PARAMETER_NAME,
    PAGE_QUERY_PARAMETER_NAME,
    SORT_QUERY_PARAMETER_NAME,
} from 'config/constants';
import { DEFAULT_PAGE_SIZE } from 'config/constants';
import { mapFlagDetailApiData } from 'connectors/flags/Flags';
import {
    FlagDetailQueryApi,
    FlagDetailQueryDocumentApi,
    FlagDetailQueryVariablesApi,
    FlagProductsQueryApi,
    FlagProductsQueryDocumentApi,
    FlagProductsQueryVariablesApi,
    useFlagDetailQueryApi,
} from 'graphql/generated';
import { createClient } from 'helpers/createClient';
import { getMappedProductFilter } from 'helpers/filterOptions/getMappedProductFilter';
import { mapParametersFilter } from 'helpers/filterOptions/MapParametersFilter';
import { initServerSideProps, ServerSidePropsType } from 'helpers/InitServerSideProps';
import { getIsRedirectedFromSsr } from 'helpers/isServer';
import { getRedirectWithOffsetPage } from 'helpers/loadMore/getRedirectWithOffsetPage';
import { getProductListSortFromUrlQuery } from 'helpers/parsing/urlParsing';
import { getUrlWithoutGetParameters } from 'helpers/parsing/urlParsing';
import { useCurrentFilterQuery } from 'helpers/queryParams/useCurrentFilterQuery';
import { useCurrentSortQuery } from 'helpers/queryParams/useCurrentSortQuery';
import { useSeoTitleWithPagination } from 'helpers/seo/useSeoTitleWithPagination';
import { getServerSidePropsWrapper } from 'helpers/serverSide/getServerSidePropsWrapper';
import { handleServerSideErrorResponseForFriendlyUrls } from 'helpers/serverSide/handleServerSideErrorResponseForFriendlyUrls';
import { useQueryError } from 'hooks/graphQl/UseQueryError';
import { useGtmFriendlyPageView } from 'hooks/gtm/useGtmFriendlyPageView';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { NextPageExtended } from 'types/global';
import { CombinedError } from 'urql';
import { getNumberFromUrlQuery } from 'utils/getNumberFromUrlQuery';
import { getSlugFromServerSideUrl, getSlugFromUrl } from 'utils/getSlugFromUrl';
import { useGtmPageViewEvent } from 'utils/Gtm/EventFactories';
import { getGtmPageInfoForFriendlyUrl } from 'utils/Gtm/Gtm';

const FlagDetailPage: NextPageExtended<ServerSidePropsType> = () => {
    const router = useRouter();
    const slug = getUrlWithoutGetParameters(router.asPath);
    const currentFilter = useCurrentFilterQuery();
    const currentSort = useCurrentSortQuery();
    const mappedProductFilter = mapParametersFilter(currentFilter);

    const [{ data: flagDetailData, fetching: isFlagDetailFetching, error }] = useFlagDetailQueryApi({
        variables: {
            urlSlug: getSlugFromUrl(slug),
            orderingMode: currentSort,
            filter: mappedProductFilter,
        },
    });
    useQueryError(error, error instanceof CombinedError);

    const mappedFlag = useMemo(() => {
        if (flagDetailData?.flag) {
            return mapFlagDetailApiData(flagDetailData.flag);
        }

        return null;
    }, [flagDetailData?.flag]);

    const seoTitle = useSeoTitleWithPagination(mappedFlag?.productConnection.totalCount, mappedFlag?.name);

    const gtmFlagUrlPageViewEvent = useGtmPageViewEvent(
        getGtmPageInfoForFriendlyUrl(mappedFlag, slug, mappedFlag?.breadcrumb),
    );
    useGtmFriendlyPageView(gtmFlagUrlPageViewEvent, slug, isFlagDetailFetching);

    return (
        <CommonLayout
            showLastVisitedProducts
            hreflangLinks={flagDetailData?.flag?.hreflangLinks}
            isFetchingData={!currentFilter && isFlagDetailFetching && !flagDetailData}
            title={seoTitle}
        >
            {mappedFlag?.breadcrumb && (
                <Webline>
                    <Breadcrumbs key="breadcrumb" breadcrumb={mappedFlag.breadcrumb} />
                </Webline>
            )}
            {mappedFlag && <FlagDetail flag={mappedFlag} />}
        </CommonLayout>
    );
};

FlagDetailPage.layout = 'plain';

export const getServerSideProps = getServerSidePropsWrapper(
    ({ domainConfig, redisClient, ssrExchange, t }) =>
        async (context) => {
            const client = createClient({
                publicGraphqlEndpoint: domainConfig.publicGraphqlEndpoint,
                ssrExchange,
                t,
                context,
                redisClient,
            });
            const orderingMode = getProductListSortFromUrlQuery(context.query[SORT_QUERY_PARAMETER_NAME]);
            const filter = getMappedProductFilter(context.query[FILTER_QUERY_PARAMETER_NAME]);
            const page = getNumberFromUrlQuery(context.query[PAGE_QUERY_PARAMETER_NAME], 1);
            const loadMore = getNumberFromUrlQuery(context.query[LOAD_MORE_QUERY_PARAMETER_NAME], 0);
            const urlSlug = getSlugFromServerSideUrl(context.req.url ?? '', context.req.headers);
            const redirect = getRedirectWithOffsetPage(page, loadMore, urlSlug, context.query);

            if (redirect) {
                return redirect;
            }

            const flagDetailResponse = await client!
                .query<FlagDetailQueryApi, FlagDetailQueryVariablesApi>(FlagDetailQueryDocumentApi, {
                    urlSlug,
                    filter,
                    orderingMode,
                })
                .toPromise();

            await client!
                .query<FlagProductsQueryApi, FlagProductsQueryVariablesApi>(FlagProductsQueryDocumentApi, {
                    endCursor: getEndCursor(page),
                    orderingMode,
                    filter,
                    urlSlug,
                    first: DEFAULT_PAGE_SIZE * (loadMore + 1),
                })
                .toPromise();

            if (getIsRedirectedFromSsr(context.req.headers)) {
                const serverSideErrorResponse = handleServerSideErrorResponseForFriendlyUrls(
                    flagDetailResponse.error?.graphQLErrors,
                    flagDetailResponse.data?.flag,
                    context.res,
                );

                if (serverSideErrorResponse) {
                    return serverSideErrorResponse;
                }
            }

            const initServerSideData = await initServerSideProps({ context, client, domainConfig, ssrExchange });

            return initServerSideData;
        },
);
export default FlagDetailPage;
